<script setup lang="ts">
import GenericLoader from '~/components/user-interface/generic-loader.vue';
import { ESumSubAction } from '~/interfaces/dto/compliance/request';
import type { SumsubComponentProps } from '~/interfaces/components/sumsub';
import type { SnsError } from '@sumsub/websdk/types/types';
import type { ISumSubGenerateTokenResponse } from '~/interfaces/dto/compliance/response';
import { globalKeys } from 'assets/data/global-keys';

const { $trackEvent } = useNuxtApp();
const { $complianceService, $authService, $storageService } = useServices();

const { initialise } = useSumsub();

const props = withDefaults(defineProps<SumsubComponentProps>(), {
  pregeneratedToken: '',
  action: ESumSubAction.None,
});

const emits = defineEmits<{
  (e: 'error', error: string): void;
  (e: 'complete'): void;
  (e: 'ready'): void;
}>();

const token = ref<string>();
const loading = ref<boolean>(true);
const initialised = ref<boolean>(false);
const sumsubElement = ref<HTMLElement>();

const fetchToken = async () => {
  try {
    let tokenResponse: ISumSubGenerateTokenResponse;
    if (
      [ESumSubAction.PassportVerify, ESumSubAction.DocumentVerify].includes(
        props.action,
      )
    )
      tokenResponse = await $complianceService.sumSubGenerateTokenFica(
        props.action,
      );
    else
      tokenResponse = await $complianceService.sumSubGenerateToken(
        props.action,
      );
    if (!!tokenResponse?.data?.token)
      $trackEvent('Account', 'MY_ACCOUNT_SUM_SUB_ACTION', {
        action: ESumSubAction[props.action],
        process: 'generate-token',
      });
    token.value = tokenResponse?.data?.token;
  } catch (e) {
    console.error(e);
    emits('error', 'token-error');
  }
};
const evaluateToken = async () => {
  try {
    if (
      [ESumSubAction.PassportVerify, ESumSubAction.DocumentVerify].includes(
        props.action,
      )
    )
      await $complianceService.sumSubGenerateTokenFicaEvaluate(
        props.action,
        token.value,
      );
    else
      await $complianceService.sumSubGenerateTokenEvaluate(
        props.action,
        token.value,
      );
    $trackEvent('Account', 'MY_ACCOUNT_SUM_SUB_ACTION', {
      action: ESumSubAction[props.action],
      process: 'evaluate-token',
    });
  } catch (e) {
    emits('error', 'token-error');
    console.error(e);
  }
};

onMounted(() => {
  if (!!props.pregeneratedToken) token.value = props.pregeneratedToken;
  else fetchToken();
});

watch(
  () => props.pregeneratedToken,
  (newToken, oldToken) => {
    if (newToken && newToken !== oldToken) {
      token.value = newToken;
    }
  },
  { immediate: true },
);

watchEffect(() => {
  if (!!token.value && !!sumsubElement.value && !initialised.value) {
    $storageService.setSessionStorage({
      key: globalKeys.authKeys.sumSubLastLivenessToken,
      data: token.value,
    });
    initialise({
      containerId: sumsubElement.value.id,
      token: token.value,
      getNewAccessToken: async () => token.value,
      onReady: () => {
        emits('ready');
        loading.value = false;
        initialised.value = true;
      },
      onError: (error: SnsError) => {
        emits('error', error.code);
        loading.value = false;
      },
      onComplete: async () => {
        emits('complete');
        if (!props.externalEvaluation) {
          await evaluateToken();
          await $authService.getUserExtended();
        }
        loading.value = false;
      },
    });
  }
});
</script>

<template>
  <div class="flex flex-center">
    <GenericLoader v-show="loading" standalone />
    <div
      v-show="!loading"
      id="sumsub-websdk-container"
      ref="sumsubElement"
      class="w-full h-full"
    ></div>
  </div>
</template>

<style scoped lang="scss">
#sumsub-websdk-container {
  iframe {
    @apply w-full h-full border-none flex-grow;
  }
}
</style>
